import { createElement } from "react";
import ReactDOM from "react-dom";
import ProvetLayout from "../modules/provetLayout";
import {
  BootstrapConfig,
  BootstrapService,
  DOMService,
  ReactRendererService
} from "./bootstrapService";

/**
 * Default implementation of DOMService using native browser APIs.
 *
 * This is the production implementation that uses the real DOM.
 * For testing, you can provide a mock implementation.
 */
export class BrowserDOMService implements DOMService {
  querySelectorAll(selector: string): Element[] {
    return Array.from(document.querySelectorAll(selector));
  }

  getElementById(id: string): HTMLElement | null {
    return document.getElementById(id);
  }

  getAttribute(element: Element, attributeName: string): string | null {
    return element.getAttribute(attributeName);
  }

  setAttribute(element: Element, attributeName: string, value: string): void {
    element.setAttribute(attributeName, value);
  }

  addEventListener(event: string, handler: () => void): void {
    document.addEventListener(event, handler);
  }
}

/**
 * Default implementation of ReactRendererService using ReactDOM.
 *
 * This is the production implementation that uses ReactDOM.render.
 * For testing, you can provide a mock implementation.
 */
export class ReactDOMRendererService implements ReactRendererService {
  render(component: React.ReactElement, container: Element): void {
    ReactDOM.render(component, container);
  }
}

/**
 * Default implementation of BootstrapService.
 *
 * This service:
 * 1. Finds all elements matching the component selector
 * 2. Parses props from data attributes
 * 3. Creates React elements
 * 4. Renders them into the DOM
 *
 * **Dependency Injection:**
 * - Takes DOMService and ReactRendererService as constructor params
 * - Allows for easy testing with mocks
 * - Decouples from specific implementations
 *
 * @example
 * ```typescript
 * // Production use
 * const domService = new BrowserDOMService();
 * const rendererService = new ReactDOMRendererService();
 * const bootstrap = new ProvetBootstrapService(domService, rendererService);
 * bootstrap.initialize(DEFAULT_BOOTSTRAP_CONFIG);
 *
 * // Testing use
 * const mockDOM = new MockDOMService();
 * const mockRenderer = new MockReactRendererService();
 * const bootstrap = new ProvetBootstrapService(mockDOM, mockRenderer);
 * bootstrap.initialize(testConfig);
 * ```
 */
export class ProvetBootstrapService implements BootstrapService {
  constructor(
    private readonly domService: DOMService,
    private readonly rendererService: ReactRendererService
  ) {}

  /**
   * Initializes React components in the DOM.
   *
   * Finds all elements matching the component selector, parses their props,
   * and renders ProvetLayout components into them.
   *
   * @param config - Bootstrap configuration
   */
  initialize(config: BootstrapConfig): void {
    const layouts = this.domService.querySelectorAll(config.componentSelector);

    if (!layouts || layouts.length === 0) {
      console.warn(
        `ProvetBootstrapService: No elements found matching selector "${config.componentSelector}"`
      );
      return;
    }

    console.log(
      `ProvetBootstrapService: Found ${layouts.length} component(s) to initialize`
    );

    for (const layout of layouts) {
      try {
        const propsJson =
          this.domService.getAttribute(layout, config.propsAttribute) || "{}";
        const props = JSON.parse(propsJson);

        const component = createElement(ProvetLayout, props);
        this.rendererService.render(component, layout);

        console.log("ProvetBootstrapService: Successfully rendered component", {
          element: layout,
          props
        });
      } catch (error) {
        console.error("ProvetBootstrapService: Failed to render component", {
          element: layout,
          error
        });
      }
    }
  }
}

/**
 * Utility service for Provet-specific DOM operations.
 *
 * Provides higher-level operations on top of the base DOMService.
 */
export class ProvetDOMUtilityService {
  constructor(private readonly domService: DOMService) {}

  /**
   * Toggles the consent/result view visibility.
   *
   * This is used to show/hide the booking confirmation after successful booking.
   */
  toggleResultView(): void {
    const element = this.domService.getElementById("c-provet-online-booking__consent");

    if (!element) {
      console.warn("ProvetDOMUtilityService: Consent element not found");
      return;
    }

    const isActive = this.domService.getAttribute(element, "data-active") === "true";
    this.domService.setAttribute(element, "data-active", isActive ? "false" : "true");

    console.log("ProvetDOMUtilityService: Toggled result view", { isActive: !isActive });
  }
}
